import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
 import Icon from'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

export default function VerifyEmail() {
  const location = useLocation()
  const navigate = useNavigate()
  const { resendVerification, signOut, user } = useAuth()
  const email = location?.state?.email || user?.email || ''
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [resendError, setResendError] = useState('')

  const handleResend = async () => {
    if (!email) return
    setResendLoading(true)
    setResendError('')
    const { error } = await resendVerification(email)
    setResendLoading(false)
    if (error) {
      setResendError(error.message)
    } else {
      setResendSuccess(true)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/login-page')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-teal-700/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/landing-page" className="inline-flex items-center gap-3 group">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-200">
              <Icon name="Zap" size={24} color="#FFFFFF" strokeWidth={2.5} />
            </div>
            <span className="text-2xl font-bold text-white font-heading">
              Fit<span className="text-teal-400">Pulse</span>
            </span>
          </Link>
        </div>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mx-auto mb-6">
            <Icon name="MailCheck" size={36} color="#2dd4bf" />
          </div>

          <h1 className="text-2xl font-bold text-white font-heading mb-2">Verify your email</h1>
          <p className="text-slate-400 text-sm font-body mb-2">
            We sent a verification link to
          </p>
          {email && (
            <p className="text-teal-400 font-medium text-sm font-caption mb-4">{email}</p>
          )}
          <p className="text-slate-500 text-xs font-body mb-8 leading-relaxed">
            Click the link in the email to activate your account. Once verified, you can sign in and access your dashboard.
          </p>

          {resendSuccess ? (
            <div className="flex items-center justify-center gap-2 text-emerald-400 text-sm font-caption mb-6">
              <Icon name="CheckCircle" size={16} />
              Verification email resent!
            </div>
          ) : (
            <>
              {resendError && (
                <p className="text-xs text-red-400 font-caption mb-3">{resendError}</p>
              )}
              <button
                onClick={handleResend}
                disabled={resendLoading}
                className="w-full h-11 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold text-sm font-caption transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-teal-900/30 mb-4"
              >
                {resendLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Resending...
                  </>
                ) : (
                  <>
                    <Icon name="RefreshCw" size={16} />
                    Resend verification email
                  </>
                )}
              </button>
            </>
          )}

          <div className="border-t border-white/10 pt-4">
            <p className="text-slate-500 text-xs font-body mb-3">
              Already verified? Try signing in again.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/login-page"
                className="text-sm text-teal-400 hover:text-teal-300 transition-colors font-caption"
              >
                Go to Login
              </Link>
              <span className="text-slate-600">·</span>
              <button
                onClick={handleSignOut}
                className="text-sm text-slate-400 hover:text-white transition-colors font-caption"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
